import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Megaphone, Play, Pause, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { campaignsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { CAMPAIGN_STATUS_LABEL, CAMPAIGN_STATUS_VARIANT, fmtRelative } from '@/lib/format';
import { NewCampaignDialog } from './NewCampaignDialog';
import { useCan } from '@/features/auth/store';

export function CampaignsPage() {
  const qc = useQueryClient();
  const canEdit = useCan('campaigns.manage');
  const canDelete = useCan('campaigns.delete');
  const q = useQuery({
    queryKey: ['campaigns'],
    queryFn: campaignsApi.list,
    refetchInterval: 15_000,
  });
  const [open, setOpen] = useState(false);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['campaigns'] });
  const start = useMutation({
    mutationFn: campaignsApi.start,
    onSuccess: () => {
      toast.success('Campanha iniciada');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });
  const pause = useMutation({
    mutationFn: campaignsApi.pause,
    onSuccess: () => {
      toast.success('Campanha pausada');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });
  const remove = useMutation({
    mutationFn: campaignsApi.remove,
    onSuccess: () => {
      toast.success('Campanha excluída');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });

  return (
    <div>
      <PageHeader
        title="Campanhas"
        description="Discagem automática em lote com controle de horário, tentativas e concorrência"
        actions={
          canEdit && (
            <Button onClick={() => setOpen(true)}>
              <Plus /> Nova campanha
            </Button>
          )
        }
      />
      {q.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
      ) : q.data?.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {q.data.map((c) => {
            const pct = c.totalLeads ? (c.completedLeads / c.totalLeads) * 100 : 0;
            return (
              <Card key={c.id} className="flex flex-col">
                <CardContent className="flex flex-1 flex-col gap-3 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        to={`/campanhas/${c.id}`}
                        className="block truncate font-semibold hover:underline"
                      >
                        {c.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {c.language} · criada {fmtRelative(c.createdAt)}
                        {c.kind === 'autopilot' && ' · piloto automático'}
                        {c.channel === 'web' && ' · navegador'}
                      </p>
                    </div>
                    <StatusBadge
                      label={CAMPAIGN_STATUS_LABEL[c.status]}
                      className={CAMPAIGN_STATUS_VARIANT[c.status]}
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                      <span>
                        {c.completedLeads} de {c.totalLeads} leads
                      </span>
                      <span>{c.recoveredLeads} recuperados</span>
                    </div>
                    <Progress value={pct} />
                  </div>
                  {(canEdit || canDelete) && (
                    <div className="mt-auto flex gap-2">
                      {!canEdit ? null : c.status === 'running' ? (
                        <Button size="sm" variant="outline" onClick={() => pause.mutate(c.id)}>
                          <Pause /> Pausar
                        </Button>
                      ) : c.status !== 'done' ? (
                        <Button size="sm" onClick={() => start.mutate(c.id)}>
                          <Play /> {c.status === 'paused' ? 'Retomar' : 'Iniciar'}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/campanhas/${c.id}`}>Detalhes</Link>
                      </Button>
                      {canDelete && c.status !== 'running' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-auto text-destructive"
                          onClick={() => confirm('Excluir campanha?') && remove.mutate(c.id)}
                        >
                          <Trash2 />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={Megaphone}
          title="Nenhuma campanha"
          description="Crie uma campanha para que a IA ligue automaticamente para um grupo de leads."
          action={
            canEdit && (
              <Button onClick={() => setOpen(true)}>
                <Plus /> Nova campanha
              </Button>
            )
          }
        />
      )}
      <NewCampaignDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
